(() => {
  const keys = ['hprice_min', 'hprice_max', 'hprice_currency', 'hprice_page'];
  function bounds(min, max) {
    const read = value => value === '' || value === null ? null : Number(value);
    const low = read(min), high = read(max);
    if ((low !== null && (!Number.isFinite(low) || low < 0)) || (high !== null && (!Number.isFinite(high) || high < 0)) || (low !== null && high !== null && low > high)) throw new Error('invalid');
    return { min: low === null ? null : Math.round(low * 100), max: high === null ? null : Math.round(high * 100) };
  }
  function matches(product, range) {
    return Number.isFinite(product.price) && (range.min === null || product.price >= range.min) && (range.max === null || product.price <= range.max);
  }
  if (typeof module !== 'undefined' && module.exports) module.exports = { bounds, matches };
  if (typeof window === 'undefined') return;
  const make = (tag, className, text) => {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (text !== undefined) element.textContent = text;
    return element;
  };
  class MarketPriceFilter extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.cache = new Map();
      this.version = 0;
      this.min = this.querySelector('[name=hprice_min]');
      this.max = this.querySelector('[name=hprice_max]');
      this.status = this.querySelector('[role=status]');
      this.form = this.querySelector('form');
      this.form.addEventListener('submit', event => {
        event.preventDefault();
        event.stopPropagation();
        if (!this.form.reportValidity()) return;
        try {
          this.range = bounds(this.min.value, this.max.value);
          if (this.range.min === null && this.range.max === null) { this.clear(); return; }
          this.active = true;
          this.page = 1;
          this.writeURL(true);
          this.syncForms();
          this.apply();
        } catch (error) { this.status.textContent = this.dataset.invalid; }
      });
      this.querySelector('[data-price-clear]').addEventListener('click', () => this.clear());
      this.readURL();
      this.observer = new MutationObserver(() => {
        this.syncForms();
        const container = document.getElementById('ProductGridContainer');
        if (this.active && container && !container.querySelector('[data-hiirad-filtered]') && !this.busy) this.apply();
      });
      const gridContainer = document.getElementById('ProductGridContainer');
      if (gridContainer) this.observer.observe(gridContainer, { childList: true, subtree: true });
      this.onPop = () => {
        this.version++;
        this.controller?.abort();
        this.readURL();
        if (this.active) this.apply();
        else this.restore();
      };
      window.addEventListener('popstate', this.onPop);
      this.syncForms();
      if (this.active) this.apply();
    }
    disconnectedCallback() {
      this.observer?.disconnect();
      this.controller?.abort();
      window.removeEventListener('popstate', this.onPop);
    }
    readURL() {
      const params = new URL(location.href).searchParams;
      const same = params.get('hprice_currency') === this.dataset.currency;
      this.min.value = same ? params.get('hprice_min') || '' : '';
      this.max.value = same ? params.get('hprice_max') || '' : '';
      try { this.range = bounds(this.min.value, this.max.value); } catch { this.range = bounds('', ''); }
      this.active = this.range.min !== null || this.range.max !== null;
      this.page = Math.max(1, Number.parseInt(params.get('hprice_page'), 10) || 1);
      if (this.active) this.querySelector('details').open = true;
    }
    writeURL(push = false) {
      const url = new URL(location.href);
      keys.forEach(key => url.searchParams.delete(key));
      url.searchParams.delete('page');
      if (this.active) {
        if (this.range.min !== null) url.searchParams.set('hprice_min', String(this.range.min / 100));
        if (this.range.max !== null) url.searchParams.set('hprice_max', String(this.range.max / 100));
        url.searchParams.set('hprice_currency', this.dataset.currency);
        if (this.page > 1) url.searchParams.set('hprice_page', String(this.page));
      }
      history[push ? 'pushState' : 'replaceState'](history.state, '', url);
    }
    syncForms() {
      document.querySelectorAll('facet-filters-form form').forEach(form => {
        keys.slice(0, 3).forEach(key => {
          let field = form.querySelector(`[data-hiirad-hidden="${key}"]`);
          const value = !this.active ? null : key === 'hprice_currency' ? this.dataset.currency : key === 'hprice_min' ? this.range.min : this.range.max;
          if (value === null) { field?.remove(); return; }
          if (!field) {
            field = make('input');
            field.type = 'hidden'; field.name = key; field.dataset.hiiradHidden = key;
            form.append(field);
          }
          const next = key === 'hprice_currency' ? value : String(value / 100);
          if (field.value !== next) field.value = next;
        });
      });
    }
    baseURL() {
      const url = new URL(location.href);
      [...keys, 'page', 'section_id', 'sections', 'filter.v.price.gte', 'filter.v.price.lte'].forEach(key => url.searchParams.delete(key));
      return url;
    }
    async fetchPage(base, page, signal) {
      const url = new URL(base);
      url.searchParams.set('section_id', 'hiirad-price-data');
      url.searchParams.set('page', String(page));
      const response = await fetch(url, { credentials: 'same-origin', signal });
      if (!response.ok) throw new Error('request');
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const source = doc.querySelector('[data-hiirad-price-data]');
      if (!source) throw new Error('missing');
      const data = JSON.parse(source.textContent);
      if (data.currency !== this.dataset.currency || data.page !== page || !Array.isArray(data.items) || !Number.isInteger(data.pages) || data.pages < 0 || data.pages > 100) throw new Error('currency');
      return data;
    }
    async load(base, signal) {
      const key = base.href + '|' + this.dataset.currency;
      if (this.cache.has(key)) return this.cache.get(key);
      const first = await this.fetchPage(base, 1, signal);
      const batches = [first.items];
      let nextPage = 2;
      const worker = async () => {
        while (nextPage <= first.pages) {
          const page = nextPage++;
          const data = await this.fetchPage(base, page, signal);
          if (data.pages !== first.pages) throw new Error('changed');
          batches[page - 1] = data.items;
        }
      };
      await Promise.all(Array.from({ length: Math.min(3, Math.max(0, first.pages - 1)) }, worker));
      const items = [...new Map(batches.flat().map(item => [item.id, item])).values()];
      if (this.cache.size >= 3) this.cache.delete(this.cache.keys().next().value);
      this.cache.set(key, items);
      return items;
    }
    async apply() {
      const token = ++this.version;
      this.controller?.abort();
      this.controller = new AbortController();
      const base = this.baseURL();
      if (this.contextURL && this.contextURL !== base.href) this.page = 1;
      this.contextURL = base.href;
      this.busy = true;
      this.status.textContent = this.dataset.loading;
      const button = this.form.querySelector('[type=submit]');
      button.disabled = true;
      const timeout = setTimeout(() => this.controller?.abort(), 45000);
      try {
        const items = await this.load(base, this.controller.signal);
        if (token !== this.version || !this.active) return;
        if (base.href !== this.baseURL().href) { this.busy = false; this.apply(); return; }
        this.items = items.filter(product => matches(product, this.range));
        this.render();
        this.writeURL();
        this.syncForms();
      } catch (error) {
        if (token === this.version) {
          this.status.textContent = this.dataset.error;
          this.controller.abort();
        }
      } finally {
        clearTimeout(timeout);
        if (token === this.version) { this.busy = false; button.disabled = false; }
      }
    }
    card(product) {
      const li = make('li', 'grid__item');
      const wrapper = make('div', 'card-wrapper product-card-wrapper underline-links-hover');
      const card = make('div', 'card card--card card--media');
      const inner = make('div', 'card__inner ratio');
      inner.style.setProperty('--ratio-percent', '0%');
      const media = make('div', 'card__media');
      const imageLink = make('a', 'media media--transparent');
      imageLink.href = product.url;
      imageLink.setAttribute('aria-label', product.title);
      if (product.image) {
        const img = make('img');
        img.src = product.image; img.alt = product.title; img.loading = 'lazy'; img.width = 600; img.height = 600;
        imageLink.append(img);
      }
      media.append(imageLink); inner.append(media);
      const content = make('div', 'card__content');
      const info = make('div', 'card__information');
      const heading = make('h3', 'card__heading h5');
      const title = make('a', 'full-unstyled-link', product.title);
      title.href = product.url; heading.append(title);
      const information = make('div', 'card-information');
      const price = make('div', 'price');
      const priceText = make('span', 'price-item price-item--regular', (product.varies ? this.dataset.from + ' ' : '') + product.money);
      price.append(priceText); information.append(price); info.append(heading, information);
      const quick = make('div', 'quick-add no-js-hidden');
      if (product.available && product.simple) {
        const form = make('form'); form.method = 'post'; form.action = this.dataset.cartAdd;
        const id = make('input'); id.type = 'hidden'; id.name = 'id'; id.value = product.variant;
        const quantity = make('input'); quantity.type = 'hidden'; quantity.name = 'quantity'; quantity.value = '1';
        const button = make('button', 'quick-add__submit button button--full-width', this.dataset.add);
        button.type = 'submit'; button.name = 'add'; button.setAttribute('aria-label', this.dataset.add + ': ' + product.title);
        form.append(id, quantity, button); quick.append(form);
      } else if (product.available) {
        const link = make('a', 'quick-add__submit button button--full-width', this.dataset.options);
        link.href = product.url; quick.append(link);
      } else {
        const button = make('button', 'quick-add__submit button button--full-width', this.dataset.sold);
        button.disabled = true; button.name = 'add'; quick.append(button);
      }
      content.append(info, quick); card.append(inner, content); wrapper.append(card); li.append(wrapper);
      return li;
    }
    render() {
      const container = document.getElementById('ProductGridContainer');
      if (!container) throw new Error('grid');
      const sourceGrid = container.querySelector('#product-grid');
      const sectionId = sourceGrid?.dataset.id || '';
      const pageCount = Math.max(1, Math.ceil(this.items.length / 50));
      this.page = Math.min(this.page, pageCount);
      const shell = make('div', 'collection page-width hiirad-price-results');
      shell.dataset.hiiradFiltered = 'true';
      const grid = make('ul', 'grid product-grid grid--2-col-tablet-down grid--5-col-desktop');
      grid.id = 'product-grid'; grid.dataset.id = sectionId; grid.setAttribute('role', 'list');
      this.items.slice((this.page - 1) * 50, this.page * 50).forEach(product => grid.append(this.card(product)));
      shell.append(grid);
      const count = this.dataset.count.replace('[count]', this.items.length);
      this.status.textContent = count;
      if (!document.querySelector('#ProductCount, #ProductCountDesktop')) shell.prepend(make('p', 'hiirad-price-filtered-count', count));
      if (pageCount > 1) {
        const nav = make('nav', 'hiirad-price-pagination');
        nav.setAttribute('aria-label', 'Pagination');
        const previous = make('button', '', this.dataset.previous), next = make('button', '', this.dataset.next);
        previous.disabled = this.page === 1; next.disabled = this.page === pageCount;
        const go = delta => { this.page += delta; this.render(); this.writeURL(true); document.getElementById('product-grid').scrollIntoView({ block: 'start' }); };
        previous.addEventListener('click', () => go(-1)); next.addEventListener('click', () => go(1));
        nav.append(previous, make('span', '', this.page + ' / ' + pageCount), next); shell.append(nav);
      }
      container.replaceChildren(shell);
      document.querySelectorAll('#ProductCount, #ProductCountDesktop').forEach(element => { element.textContent = count; });
    }
    async restore() {
      const token = ++this.version;
      this.controller?.abort();
      this.controller = new AbortController();
      this.busy = true;
      try {
        const base = this.baseURL();
        const response = await fetch(base, { signal: this.controller.signal, credentials: 'same-origin' });
        if (!response.ok) throw new Error('restore');
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const fresh = doc.getElementById('ProductGridContainer');
        const container = document.getElementById('ProductGridContainer');
        if (!fresh || !container) throw new Error('restore');
        if (token !== this.version) return;
        container.replaceChildren(...fresh.childNodes);
        document.querySelectorAll('#ProductCount, #ProductCountDesktop').forEach(element => { element.innerHTML = doc.getElementById(element.id)?.innerHTML || ''; });
        this.status.textContent = '';
      } catch (error) { if (token === this.version) this.status.textContent = this.dataset.error; }
      finally { if (token === this.version) this.busy = false; }
    }
    clear() {
      this.active = false; this.range = bounds('', ''); this.min.value = ''; this.max.value = ''; this.page = 1;
      this.writeURL(true); this.syncForms(); this.restore();
    }
  }
  if (!customElements.get('hiirad-price-filter')) customElements.define('hiirad-price-filter', MarketPriceFilter);
  const mount = () => {
    if (document.querySelector('hiirad-price-filter')) return;
    if (document.querySelector('input[name="filter.v.price.gte"]')) return;
    const template = document.getElementById('hiirad-price-filter-template');
    const target = document.getElementById('main-collection-filters') || document.getElementById('main-search-filters');
    if (template && target) target.prepend(template.content.cloneNode(true));
  };
  mount();
  document.addEventListener('shopify:section:load', mount);
})();
